// Line check photos (migration 034): the shapes the payloads carry, when a photo
// is offered, and the tablet's own cache of REFERENCE photo bytes.
//
// A photo is evidence, never a judgement: nothing here compares a reference with
// what was seen, and nothing gates on either. Rule 15: a list the server did not
// send (`undefined` / `null`) is "not known"; `[]` is none.
//
// ⚠ NOT THE SERVICE WORKER. The API is another origin the SW never intercepts,
// the bytes need the bearer token, and "/api is NEVER cached" stays whole. The
// reference bytes are immutable (keyed photoId + sha256), so they live in this
// app's own IndexedDB, fetched with api.blob, and evicted once a payload stops
// listing them. Actual photos are never cached: they are a record, read fresh.
import { api } from '../../lib/api';

/** A reference photo on a spec parameter: "what good looks like". */
export interface ReferencePhoto {
  photoId: number;
  caption: string | null;
  sha256: string;
}

/** An actual photo on a reading: what one person saw at one moment. */
export interface ReadingPhoto {
  photoId: number;
  uploadedByUpn: string | null;
  capturedAtDevice: string | null;
}

/** The server's most per reading (409 photo_limit past it). */
export const MAX_LINE_PHOTOS = 4;

/** A photo is offered only where the answer is bad: OUT or ACTION. Never on IN, never on no preview. */
export function offerPhotoForVerdict(preview: 'IN' | 'ACTION' | 'OUT' | null): boolean {
  return preview === 'OUT' || preview === 'ACTION';
}

/**
 * Where a photo of an answer goes, or null when this person cannot add one. The answer queued on
 * this device (its client id), else the server's reading - but only the reading's own capturer may
 * add a photo to it (the server refuses anyone else `not_your_reading`, rule 31). Offering the
 * picker on a colleague's No only let the next operator take a photo nobody could send (audit
 * 28 Oct 2026). Capturer unknown is not "mine" (rule 15).
 */
export function photoTarget(
  localClientCaptureId: string | null | undefined,
  reading: { id: number | string; capturedByUpn: string | null } | null,
  signedInUpn: string,
): string | number | null {
  if (localClientCaptureId) return localClientCaptureId;
  if (!reading) return null;
  const id = Number(reading.id);
  if (!Number.isFinite(id)) return null;
  const me = signedInUpn.trim().toLowerCase();
  const by = (reading.capturedByUpn ?? '').trim().toLowerCase();
  return me !== '' && by === me ? id : null;
}

export const refPhotoPath = (specParameterId: number, photoId: number) =>
  `/spec-parameters/${specParameterId}/photos/${photoId}`;
export const readingPhotoPath = (readingId: number | string, photoId: number) =>
  `/readings/${readingId}/photos/${photoId}`;

/* ── The reference cache ─────────────────────────────── */

const DB_NAME = 'quality_refphotos';
const STORE = 'photos';

/** Which payload listed the photo. Each screen evicts only its own kind, so the
 *  walk-around (LINE spec) and the keypad (PRODUCT spec) do not evict each other. */
export type RefSource = 'readiness' | 'spec';

interface CachedRef {
  key: string;
  source: RefSource;
  blob: Blob;
}

const keyOf = (photoId: number, sha256: string) => `${photoId}:${sha256.trim().toLowerCase()}`;

let dbPromise: Promise<IDBDatabase> | null = null;
function open(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('no IndexedDB'));
  return (dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => {
      // A later version of this store opening in another tab: let go, or it waits for ever.
      req.result.onversionchange = () => {
        req.result.close();
        dbPromise = null;
      };
      resolve(req.result);
    };
    // Another tab holds an older version open: a cache miss, never a hang (the bytes are fetched).
    req.onblocked = () => {
      dbPromise = null;
      reject(new Error('The photo cache is open in another tab.'));
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  }));
}

async function run<T>(mode: IDBTransactionMode, issue: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const r = issue(db.transaction(STORE, mode).objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/**
 * The bytes of a reference photo: from the cache, else from the API (then kept).
 * A cache that cannot be read or written is only slower - never an error here.
 */
export async function referencePhotoBlob(
  specParameterId: number,
  photo: ReferencePhoto,
  source: RefSource,
  signal?: AbortSignal,
): Promise<Blob> {
  const key = keyOf(photo.photoId, photo.sha256);
  try {
    const hit = (await run('readonly', (s) => s.get(key) as IDBRequest<CachedRef | undefined>)) ?? null;
    if (hit?.blob) return hit.blob;
  } catch {
    // no cache on this device: fetch
  }
  const blob = await api.blob(refPhotoPath(specParameterId, photo.photoId), signal);
  try {
    await run('readwrite', (s) => s.put({ key, source, blob } satisfies CachedRef));
  } catch {
    // not kept; it is fetched again next time
  }
  return blob;
}

/** Drop this source's cached photos the payload no longer lists (a retired photo, a new spec version). */
export async function evictReferencePhotos(source: RefSource, listed: ReadonlyArray<ReferencePhoto>): Promise<void> {
  const keep = new Set(listed.map((p) => keyOf(p.photoId, p.sha256)));
  try {
    const all = (await run('readonly', (s) => s.getAll() as IDBRequest<CachedRef[]>)) ?? [];
    for (const row of all) {
      if (row.source === source && !keep.has(row.key)) await run('readwrite', (s) => s.delete(row.key));
    }
  } catch {
    // nothing cached, or no cache: nothing to evict
  }
}

/** Every reference photo a payload lists, for eviction. A null list is not known, so it lists nothing to keep - and evicts nothing. */
export function listedReferences(
  lists: ReadonlyArray<ReadonlyArray<ReferencePhoto> | null | undefined>,
): ReferencePhoto[] | null {
  if (lists.some((l) => l == null)) return null;
  return lists.flatMap((l) => l ?? []);
}
